// ── Unit System ──
// Centralized conversion + formatting utilities.
// All internal data stays metric; conversion happens at display time only.

export type UnitSystem = 'metric' | 'imperial';

const STORAGE_KEY = 'divecheck-units';

export const getStoredUnitSystem = (): UnitSystem => {
  const saved = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  return saved === 'imperial' ? 'imperial' : 'metric';
};

export const storeUnitSystem = (system: UnitSystem): void => {
  localStorage.setItem(STORAGE_KEY, system);
};

// ── Conversions ──

export const kmToMiles = (km: number): number => km * 0.621371;
export const milesToKm = (miles: number): number => miles / 0.621371;

export const metersToFeet = (m: number): number => m * 3.28084;
export const feetToMeters = (ft: number): number => ft / 3.28084;

export const celsiusToFahrenheit = (c: number): number => (c * 9) / 5 + 32;
export const fahrenheitToCelsius = (f: number): number => ((f - 32) * 5) / 9;

// ── Display formatters ──

export const formatDistance = (km: number, system: UnitSystem): string => {
  if (system === 'imperial') {
    return `${kmToMiles(km).toFixed(1)} mi`;
  }
  return `${km.toFixed(1)} km`;
};

export const formatDepth = (meters: number, system: UnitSystem): string => {
  if (system === 'imperial') {
    return `${Math.round(metersToFeet(meters))} ft`;
  }
  return `${meters} m`;
};

/** Format a depth range like "0–25m" → "0–82ft" */
export const formatDepthRange = (range: string, system: UnitSystem): string => {
  if (system === 'metric') return range;
  return range.replace(/(\d+)\s*m/g, (_, num) => `${Math.round(metersToFeet(Number(num)))} ft`);
};

export const formatTemp = (celsius: number, system: UnitSystem): string => {
  if (system === 'imperial') {
    return `${Math.round(celsiusToFahrenheit(celsius))}°F`;
  }
  return `${celsius}°C`;
};

/** Short temp without unit label (e.g. "14°") */
export const formatTempShort = (celsius: number, system: UnitSystem): string => {
  if (system === 'imperial') {
    return `${Math.round(celsiusToFahrenheit(celsius))}°`;
  }
  return `${celsius}°`;
};

export const formatWindSpeed = (kmh: number, system: UnitSystem): string => {
  if (system === 'imperial') {
    return `${Math.round(kmh * 0.621371)} mph`;
  }
  return `${kmh} km/h`;
};

/** Unit labels for UI selectors */
export const unitLabels = (system: UnitSystem) => ({
  distance: system === 'imperial' ? 'mi' : 'km',
  depth: system === 'imperial' ? 'ft' : 'm',
  temp: system === 'imperial' ? '°F' : '°C',
  speed: system === 'imperial' ? 'mph' : 'km/h',
});
